import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, Icon, Loader, Text, useTheme } from '@Sage/hoi-poi-ui';

import styles from './styles.js';

const useStyles = createUseStyles(styles, 'SyncStepper');

const STEP_STATUS = {
    COMPLETED: 'completed',
    ACTIVE: 'active',
    PENDING: 'pending',
};

const SyncStepIndicator = ({ status, classes }) => {
    if (status === STEP_STATUS.COMPLETED) {
        return (
            <div className={classnames(classes.indicator, classes.indicatorCompleted)}>
                <Icon name="check" className={classes.checkIcon} color="white" />
            </div>
        );
    }

    if (status === STEP_STATUS.ACTIVE) {
        return (
            <div className={classnames(classes.indicator, classes.indicatorActive)}>
                <Loader size="tiny" color="primary" />
            </div>
        );
    }

    return <div className={classnames(classes.indicator, classes.indicatorPending)} />;
};

SyncStepIndicator.propTypes = {
    classes: PropTypes.object.isRequired,
    status: PropTypes.oneOf(Object.values(STEP_STATUS)).isRequired,
};

const SyncStepLabel = ({ label, status, classes, theme }) => {
    if (status === STEP_STATUS.PENDING) {
        return (
            <Text type="body" color={theme.colors.utility.textDisabled}>
                {label}
            </Text>
        );
    }

    if (status === STEP_STATUS.COMPLETED) {
        return (
            <Text type="body" color={theme.colors.utility.textPrimary}>
                {label}
            </Text>
        );
    }

    return <span className={classes.activeLabelSweep}>{label}</span>;
};

SyncStepLabel.propTypes = {
    classes: PropTypes.object.isRequired,
    label: PropTypes.string.isRequired,
    status: PropTypes.oneOf(Object.values(STEP_STATUS)).isRequired,
    theme: PropTypes.object.isRequired,
};

const SyncStepper = ({ className, steps }) => {
    const classes = useStyles();
    const theme = useTheme();

    return (
        <ul className={classnames(classes.root, className)}>
            {steps.map((step, index) => (
                <li key={step.label ?? index} className={classes.item}>
                    <SyncStepIndicator status={step.status} classes={classes} />
                    <SyncStepLabel
                        label={step.label}
                        status={step.status}
                        classes={classes}
                        theme={theme}
                    />
                </li>
            ))}
        </ul>
    );
};

SyncStepper.propTypes = {
    className: PropTypes.string,
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            status: PropTypes.oneOf(Object.values(STEP_STATUS)).isRequired,
        }),
    ).isRequired,
};

SyncStepper.displayName = 'SyncStepper';

export { STEP_STATUS };
export default memo(SyncStepper);
